export function getToken(): string | null {
  return localStorage.getItem('token');
}

export function clearToken(): void {
  localStorage.removeItem('token');
}

/**
 * WHO THE STORED TOKEN IS FOR, read from its own `sub` claim.
 *
 * Reading, not verifying — the same bargain `isTokenExpired` makes below, and for the same
 * reason: only the server can say a token is valid, and this answers a narrower question that the
 * client can settle on its own.
 *
 * It exists so anything cached per-teacher can be KEYED, rather than kept under one name and
 * handed to whoever opens the browser next. A null answer means "do not use a cache", which is
 * the safe reading of a missing or unreadable token.
 */
export function tokenSubject(): string | null {
  try {
    // The READ is inside the try as well, not just the parse. `localStorage` is not merely empty
    // where a browser has storage disabled — the accessor itself throws, and this is called during
    // render, so an unguarded read white-screens the app rather than degrading.
    const token = getToken();
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as { sub?: unknown };
    return typeof payload.sub === 'string' && payload.sub ? payload.sub : null;
  } catch {
    return null;
  }
}

/**
 * Whether the stored token has run out, read from its own `exp` claim.
 *
 * Reading, not verifying: only the server can say a token is VALID, and it does not need our help
 * to. What this answers is the narrower question of whether the token is spent, which is the one
 * thing the client can know on its own and the only justification for throwing it away.
 *
 * An unreadable token counts as expired — a malformed token is not something a user can act on, so
 * clearing it and asking them to sign in again is the only useful response.
 *
 * No token at all is NOT expired: there is nothing to have run out. Callers that need "is there a
 * usable token" want `isAuthenticated`.
 */
export function isTokenExpired(): boolean {
  const token = getToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as { exp?: number };
    return payload.exp !== undefined && payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

export function isAuthenticated(): boolean {
  if (!getToken()) return false;
  if (isTokenExpired()) {
    clearToken();
    return false;
  }
  return true;
}
